import React from 'react';
import type { WhatsAppThreadMessage } from '../../models/MasterAdmin/whatsappHub';
import { buildPreviewMessage } from './whatsappPreview';
import { formatWaTime, waBubbleIn, waBubbleOut } from './whatsappUi';

import {
  applyDoctorHonorificDedupeToBodyValues,
  dedupeDoctorHonorificInFullText,
} from './whatsappDoctorHonorific';

function messageText(m: WhatsAppThreadMessage): string {
  let text = '';
  const rendered = (m.renderedBody || '').trim();
  if (rendered && !/^\[Template:/i.test(rendered)) {
    text = rendered;
  } else if (m.templateBodyPreview && m.templateBodyValues?.length) {
    const cleanedValues = applyDoctorHonorificDedupeToBodyValues({
      bodyPreview: m.templateBodyPreview,
      values: m.templateBodyValues,
    });
    text = buildPreviewMessage(m.templateBodyPreview, cleanedValues);
  } else {
    const body = (m.body || '').trim();
    const legacy = body.match(/^\[Template:[^\]]+\]\s*(.*)$/is);
    text = legacy?.[1] ? legacy[1].trim() : body;
  }

  return dedupeDoctorHonorificInFullText(text);
}

function normalizeStatus(status?: string | null): string {
  return String(status || 'sent').toLowerCase();
}

function statusLabel(status?: string | null, isOut?: boolean): string {
  const s = normalizeStatus(status);
  if (!isOut && s === 'received') return '';
  if (s === 'read') return 'Read';
  if (s === 'delivered') return 'Delivered';
  if (s === 'failed') return 'Failed';
  if (s === 'received') return 'Received';
  if (s === 'sent') return 'Sent';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function statusTone(status: string, isOut: boolean): string {
  if (status === 'failed') return isOut ? 'text-[#ff8a8a]' : 'text-red-500 dark:text-red-400';
  if (status === 'read') return 'text-[#53bdeb]';
  return isOut ? 'text-[#8696a0]' : 'text-gray-500 dark:text-[#8696a0]';
}

function tickTone(status: string): string {
  if (status === 'failed') return 'text-[#ff8a8a]';
  if (status === 'read') return 'text-[#53bdeb]';
  return 'text-[#8696a0]';
}

function tickMarks(status: string): string | null {
  if (status === 'failed') return '✗';
  if (status === 'read' || status === 'delivered') return '✓✓';
  if (status === 'sent') return '✓';
  return null;
}

function parseWhatsAppTokens(str: string): Array<{ type: 'text' | 'bold' | 'italic' | 'strike' | 'code'; value: string }> {
  if (!str) return [];
  const tokens: Array<{ type: 'text' | 'bold' | 'italic' | 'strike' | 'code'; value: string }> = [];

  const regex = /(\*([^*]+)\*|_([^_]+)_|~([^~]+)~|`([^`]+)`)/g;
  let lastIdx = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(str)) !== null) {
    if (match.index > lastIdx) {
      tokens.push({ type: 'text', value: str.slice(lastIdx, match.index) });
    }

    if (match[2]) {
      tokens.push({ type: 'bold', value: match[2] });
    } else if (match[3]) {
      tokens.push({ type: 'italic', value: match[3] });
    } else if (match[4]) {
      tokens.push({ type: 'strike', value: match[4] });
    } else if (match[5]) {
      tokens.push({ type: 'code', value: match[5] });
    }

    lastIdx = regex.lastIndex;
  }

  if (lastIdx < str.length) {
    tokens.push({ type: 'text', value: str.slice(lastIdx) });
  }

  return tokens;
}

function renderFormattedWhatsAppText(text?: string | null): React.ReactNode {
  if (!text) return '(No message text)';

  const lines = text.split('\n');

  return (
    <>
      {lines.map((line, lIdx) => {
        const tokens = parseWhatsAppTokens(line);
        return (
          <React.Fragment key={lIdx}>
            {lIdx > 0 && <br />}
            {tokens.map((token, tIdx) => {
              if (token.type === 'bold') {
                return <strong key={tIdx} className="font-semibold">{token.value}</strong>;
              }
              if (token.type === 'italic') {
                return <em key={tIdx} className="italic">{token.value}</em>;
              }
              if (token.type === 'strike') {
                return <del key={tIdx} className="line-through">{token.value}</del>;
              }
              if (token.type === 'code') {
                return (
                  <code key={tIdx} className="rounded bg-black/10 px-1 font-mono text-[11px] dark:bg-white/10">
                    {token.value}
                  </code>
                );
              }
              return <span key={tIdx}>{token.value}</span>;
            })}
          </React.Fragment>
        );
      })}
    </>
  );
}

const WhatsAppInboxMessageBubble: React.FC<{ message: WhatsAppThreadMessage }> = ({ message: m }) => {
  const isOut = m.direction === 'outbound';
  const text = messageText(m);
  const status = normalizeStatus(m.status);
  const label = statusLabel(m.status, isOut);
  const metaTone = statusTone(status, isOut);
  const ticks = isOut ? tickMarks(status) : null;
  const isImage =
    m.headerMediaUrl &&
    (m.headerMimeType?.startsWith('image/') ||
      /\.(png|jpe?g|gif|webp)(\?|$)/i.test(m.headerMediaUrl));
  const isVideo =
    m.headerMediaUrl &&
    (m.headerMimeType?.startsWith('video/') ||
      /\.(mp4|3gp|webm|mov)(\?|$)/i.test(m.headerMediaUrl));
  const isPdf =
    m.headerMediaUrl &&
    (m.headerMimeType === 'application/pdf' || /\.pdf(\?|$)/i.test(m.headerMediaUrl));

  return (
    <div className={`flex flex-col ${isOut ? 'items-end' : 'items-start'}`}>
      {!isOut && (
        <p className="mb-0.5 px-1 text-[10px] font-medium text-[#25D366]">Customer reply</p>
      )}
      <div className={isOut ? waBubbleOut : waBubbleIn}>
        {m.templateName && isOut && /^\[Template:/i.test(m.body || '') && (
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[#128C7E]/80 dark:text-[#53bdeb]/80">
            Template · {m.templateName}
          </p>
        )}

        {m.headerMediaUrl && (
          <div className="mb-2 overflow-hidden rounded-lg border border-black/10 bg-black/5 dark:border-white/10 dark:bg-black/30">
            {isImage ? (
              <img src={m.headerMediaUrl} alt="Attachment" className="max-h-56 w-full object-contain" />
            ) : isVideo ? (
              <video src={m.headerMediaUrl} controls className="max-h-56 w-full rounded-md object-contain" />
            ) : (
              <a
                href={m.headerMediaUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 p-2.5 text-xs font-semibold text-[#128C7E] hover:underline dark:text-[#53bdeb]"
              >
                {isPdf ? '📄 PDF Document attachment' : '📎 View attachment'}
              </a>
            )}
          </div>
        )}

        <div className="whitespace-pre-wrap break-words">{renderFormattedWhatsAppText(text)}</div>

        <div className={`mt-1 flex items-center justify-end gap-1.5 text-[11px] ${metaTone}`}>
          {label ? <span>{label}</span> : null}
          {label ? <span aria-hidden="true">·</span> : null}
          <span>{formatWaTime(m.createdAt)}</span>
          {ticks ? <span className={`ml-0.5 ${tickTone(status)}`}>{ticks}</span> : null}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppInboxMessageBubble;
